import {
  Document2Record,
  useDownloadAccountDocumentMutation,
  useDownloadPersonalDocumentMutation,
  usePreviewAccountDocumentMutation,
  usePreviewPersonalDocumentMutation,
} from '@/store/documentsApi';
import {
  getDocumentDownloadName,
  getDocumentFilenameForBlobHandling,
} from './documentVaultGridUtils';

interface UsePersonalStorageRowActionArgs {
  investorId?: number;
  wealthAccountId?: number;
}

/**
 * Personal Storage rows carry a VaultFiles id in `VaultFile2Id`, so preview and
 * download go through the personal/account endpoints instead of Documents2.
 */
export const usePersonalStorageRowAction = ({
  investorId,
  wealthAccountId,
}: UsePersonalStorageRowActionArgs) => {
  const [previewPersonalDoc, { isLoading: isPreviewingPersonal }] =
    usePreviewPersonalDocumentMutation();
  const [downloadPersonalDoc, { isLoading: isDownloadingPersonal }] =
    useDownloadPersonalDocumentMutation();
  const [previewAccountDoc, { isLoading: isPreviewingAccount }] =
    usePreviewAccountDocumentMutation();
  const [downloadAccountDoc, { isLoading: isDownloadingAccount }] =
    useDownloadAccountDocumentMutation();

  const isPersonalStorageActionPending =
    isPreviewingPersonal ||
    isDownloadingPersonal ||
    isPreviewingAccount ||
    isDownloadingAccount;

  const runPersonalStorageRowAction = async (
    action: 'preview' | 'download' | 'folderClick',
    data: Document2Record,
  ) => {
    const fileName = getDocumentFilenameForBlobHandling(data);
    const vaultId = data.VaultFile2Id;
    if (action === 'preview') {
      await (
        wealthAccountId
          ? previewAccountDoc({
              wealthAccountId,
              vaultId,
              fileName,
              InvestorId: investorId,
            })
          : previewPersonalDoc({
              vaultId,
              InvestorId: investorId,
              fileName,
            })
      ).unwrap();
    } else if (action === 'download') {
      await (
        wealthAccountId
          ? downloadAccountDoc({
              wealthAccountId,
              vaultId,
              filename: getDocumentDownloadName(data),
              InvestorId: investorId,
            })
          : downloadPersonalDoc({
              vaultId,
              filename: getDocumentDownloadName(data),
              InvestorId: investorId,
            })
      ).unwrap();
    }
  };

  return { runPersonalStorageRowAction, isPersonalStorageActionPending };
};
